import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PROJECTS, WORKFLOW_SEQUENCE, PROJECT_CATEGORIES, PROJECT_STATUS_COLORS, canStartProject, SPECIALIST_LANES } from "../config/wiseboardConfig";
import EvidenceMatrixTracker from "../components/EvidenceMatrixTracker";
import SpecialistRosterManager from "../components/SpecialistRosterManager";
import ComplianceTrafficLight from "../components/ComplianceTrafficLight";
import CaseReadinessPanel from "../components/CaseReadinessPanel";
import CaseTimeline from "../components/CaseTimeline";
import { exportCasePdf } from "../utils/exportCasePdf";
import { toast } from "sonner";
import { FileDown } from "lucide-react";
import type { ProjectStatus } from "../config/wiseboardConfig";

interface ProjectRow {
  id: string;
  project_type: string;
  status: string;
}

interface RosterRow {
  lane: string;
  selected: boolean;
}

export default function CasePipeline() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const [caseTitle, setCaseTitle] = useState("");
  const [boardQuestion, setBoardQuestion] = useState("");
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [roster, setRoster] = useState<RosterRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const [refreshKey, setRefreshKey] = useState(0);

  const loadData = async () => {
    if (!caseId) return;
    const [caseRes, projRes, rosterRes] = await Promise.all([
      supabase.from("wiseboard_cases").select("title, board_question").eq("id", caseId).single(),
      supabase.from("wiseboard_projects").select("id, project_type, status").eq("case_id", caseId),
      supabase.from("wiseboard_specialist_roster").select("lane, selected").eq("case_id", caseId),
    ]);
    if (caseRes.data) { setCaseTitle(caseRes.data.title); setBoardQuestion(caseRes.data.board_question || ""); }
    if (projRes.data) setProjects(projRes.data);
    if (rosterRes.data) setRoster(rosterRes.data);
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [caseId]);

  // Realtime subscriptions — refresh pipeline + child panels on any change
  useEffect(() => {
    if (!caseId) return;

    const channel = supabase
      .channel(`pipeline-${caseId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'wiseboard_projects', filter: `case_id=eq.${caseId}` }, () => {
        loadData();
        setRefreshKey(k => k + 1);
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'wiseboard_evidence_matrix', filter: `case_id=eq.${caseId}` }, () => {
        setRefreshKey(k => k + 1);
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'wiseboard_artifacts' }, () => {
        setRefreshKey(k => k + 1);
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'wiseboard_specialist_roster', filter: `case_id=eq.${caseId}` }, () => {
        loadData();
        setRefreshKey(k => k + 1);
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [caseId]);

  const statusMap: Record<string, ProjectStatus> = {};
  projects.forEach(p => { statusMap[p.project_type] = p.status as ProjectStatus; });

  const selectedLanes = roster.filter(r => r.selected).map(r => r.lane);

  if (loading) return <div className="p-6 text-muted-foreground">Loading pipeline...</div>;

  // Group workflow for visual display
  const controlPhase = ['P0', 'P1', 'P1B', 'P2_PASS1'];
  const specialistPhase = SPECIALIST_LANES;
  const closePhase = ['P2_PASS2', 'P20', 'P21', 'P22'];

  const renderNode = (projectId: string) => {
    const project = PROJECTS[projectId];
    if (!project) return null;
    const status = statusMap[projectId] || 'not_started';
    const catInfo = PROJECT_CATEGORIES[project.category];
    const { canStart, blockers } = canStartProject(projectId, statusMap, selectedLanes);
    const isSpecialist = SPECIALIST_LANES.includes(projectId);
    const isSelected = !isSpecialist || selectedLanes.includes(projectId);
    const path = `/wiseboard/case/${caseId}/${projectId.toLowerCase().replace('_', '-')}`;

    return (
      <div
        key={projectId}
        onClick={() => navigate(path)}
        className={`relative rounded-lg border-2 p-3 cursor-pointer transition-all hover:shadow-md ${
          !isSelected ? 'opacity-40 border-dashed' : canStart || status !== 'not_started' ? `${catInfo.borderColor}` : 'border-muted'
        }`}
      >
        <div className="flex items-center gap-2 mb-1">
          <div className={`h-3 w-3 rounded-full ${catInfo.color} shrink-0`} />
          <span className="font-semibold text-sm">{projectId.replace('_', ' ')}</span>
        </div>
        <p className="text-xs text-muted-foreground truncate">{project.name}</p>
        <Badge className={`mt-2 text-[10px] ${PROJECT_STATUS_COLORS[status]}`}>
          {status.replace('_', ' ')}
        </Badge>
      </div>
    );
  };



  const handleExport = async () => {
    if (!caseId) return;
    setExporting(true);
    try {
      await exportCasePdf(caseId);
      toast.success("PDF report downloaded");
    } catch (err: any) {
      toast.error("Export failed: " + (err.message || "Unknown error"));
    }
    setExporting(false);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{caseTitle}</h1>
          {boardQuestion && <p className="text-muted-foreground mt-1 text-sm">{boardQuestion}</p>}
        </div>
        <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting}>
          <FileDown className="h-4 w-4 mr-1.5" />
          {exporting ? "Exporting..." : "Export PDF"}
        </Button>
      </div>

      {/* Case Readiness Summary */}
      <CaseReadinessPanel caseId={caseId!} refreshKey={refreshKey} />

      {/* Control & Framing Phase */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-muted-foreground uppercase tracking-wide">Control · Framing · Evidence</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {controlPhase.map(renderNode)}
          </div>
        </CardContent>
      </Card>

      {/* Specialist Roster Manager */}
      <SpecialistRosterManager
        caseId={caseId!}
        onRosterChange={async () => {
          const { data } = await supabase.from("wiseboard_specialist_roster")
            .select("lane, selected").eq("case_id", caseId);
          if (data) setRoster(data);
        }}
      />

      {/* Specialist Lanes */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-muted-foreground uppercase tracking-wide">Specialist Lanes (P3–P11)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {specialistPhase.map(renderNode)}
          </div>
        </CardContent>
      </Card>

      {/* Synthesis → Decision → Activation */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-muted-foreground uppercase tracking-wide">Verification · Synthesis · Decision · Activation</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {closePhase.map(renderNode)}
          </div>
        </CardContent>
      </Card>

      {/* Evidence Matrix */}
      <EvidenceMatrixTracker caseId={caseId!} refreshKey={refreshKey} />

      {/* Compliance Traffic Light */}
      <ComplianceTrafficLight caseId={caseId!} refreshKey={refreshKey} />

      {/* Case Timeline */}
      <CaseTimeline caseId={caseId!} refreshKey={refreshKey} />
    </div>
  );
}
